import { showUserAlert } from "../utils/userErrorMessage";
// src/pages/Safety.jsx
import React, { useEffect, useState } from "react";
import Header from "../Component/Header";
import { supabase } from "../supabase/client";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";

const ISSUES = ["민원", "사고", "과태료"];
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

export default function Safety() {
  const company = localStorage.getItem("email") || "";
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [loading, setLoading] = useState(false);

  // 표 행
  const [rows, setRows] = useState([]);
  // 열 합계
  const [colTotals, setColTotals] = useState(() => initColTotals());
  // ▶ 각 셀의 실제 레코드 목록: key = `${driver}__${car}__${month}__${issue}` => [{id, date, summary}]
  const [cellItems, setCellItems] = useState({});

  const navigate = useNavigate();

  function initColTotals() {
    const t = {};
    for (const m of MONTHS) t[m] = Object.fromEntries(ISSUES.map(i => [i, 0]));
    t.total = 0;
    return t;
  }

  // KST 기준의 1년 범위 → UTC ISO
  const yStart = new Date(`${year}-01-01T00:00:00+09:00`).toISOString();
  const yEnd   = new Date(`${year + 1}-01-01T00:00:00+09:00`).toISOString();

  const fetchAll = async () => {
    if (!company) {
      showUserAlert("회사 정보를 확인할 수 없습니다. 다시 로그인해 주세요.");
      return;
    }
    setLoading(true);
    try {
      // 1) 기사 목록
      const { data: profs, error: profErr } = await supabase
        .from("profiles")
        .select("id, company, role, driver_name, car_number")
        .eq("company", company)
        .eq("role", "driver");
      if (profErr) throw profErr;

      // 2) 보조: cars 매핑
      const { data: cars } = await supabase
        .from("cars")
        .select("car_number_only, driver, company")
        .eq("company", company);
      const carByDriver = {};
      (cars || []).forEach(c => {
        const key = (c?.driver || "").trim();
        if (key && !carByDriver[key]) carByDriver[key] = c.car_number_only || "";
      });
      // 3) 올해 safety
      const { data: safety, error: safErr } = await supabase
        .from("safety")
        .select("id, company, driver_name, car_number, issue, date, summary")
        .eq("company", company)
        .gte("date", yStart)
        .lt("date", yEnd);
      if (safErr) throw safErr;
      

      // 4) 기본 행 구성
      const baseRows = (profs || []).map(p => {
        const driver_name = (p.driver_name || p.name || "").trim();
        const car_number = (p.car_number || carByDriver[driver_name] || "").trim();
        const counts = {};
        for (const m of MONTHS) counts[m] = Object.fromEntries(ISSUES.map(i => [i, 0]));
        return { driver_name, car_number, counts, total: 0 };
      });

      // 5) 인덱스
      const idx = {};
      if (process.env.NODE_ENV !== "production") console.log("baseRows", baseRows);
      baseRows.forEach((r, i) => {
        idx[`${r.driver_name}__${r.car_number}`] = i;
      });

      // 6) 집계 + 셀 아이템 구성
      const itemsAcc = {};
      for (const s of safety || []) {
        if (process.env.NODE_ENV !== "production") console.log("safety record", s);
        const driver = (s.driver_name || "").trim();
        const car = (s.car_number || "").trim();
        const mm = new Date(s.date).getMonth() + 1;
        const issue = (s.issue || "").trim();
        if (!MONTHS.includes(mm)) continue;
        if (!ISSUES.includes(issue)) continue;
        if (process.env.NODE_ENV !== "production") console.log("processing", driver, car, mm, issue);

        const keyRow = `${driver}__${car}`;
        if (process.env.NODE_ENV !== "production") console.log("keyRow", keyRow, idx[keyRow]);
        const i = idx[keyRow];
        if (i === undefined) continue;

        baseRows[i].counts[mm][issue] += 1;

        const keyCell = `${driver}__${car}__${mm}__${issue}`;
        if (process.env.NODE_ENV !== "production") console.log("keyCell", keyCell, s.summary);
        if (!itemsAcc[keyCell]) itemsAcc[keyCell] = [];
        itemsAcc[keyCell].push({ id: s.id, date: s.date, summary: s.summary || "" });
      }

      // 7) 합계
      const totals = initColTotals();
      for (const r of baseRows) {
        let rowTotal = 0;
        for (const m of MONTHS) {
          for (const iss of ISSUES) {
            const n = r.counts[m][iss] || 0;
            if (n > 0) {
              totals[m][iss] += n;
              rowTotal += n;
            }
          }
        }
        r.total = rowTotal;
      }
      totals.total = baseRows.reduce((acc, r) => acc + (r.total || 0), 0);

      // 8) 정렬
      baseRows.sort((a, b) => {
        const c1 = (a.driver_name || "").localeCompare(b.driver_name || "", "ko-KR");
        if (c1 !== 0) return c1;
        return (a.car_number || "").localeCompare(b.car_number || "", "ko-KR", { numeric: true });
      });
      setRows(baseRows);
      setColTotals(totals);
      setCellItems(itemsAcc);
    } catch (e) {
      console.error(e);
      showUserAlert("안전 관리 데이터 조회 중 오류: " + (e.message || e));
      setRows([]);
      setColTotals(initColTotals());
      setCellItems({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const printVal = (n) => (n > 0 ? n : "");

  // ▶ 셀 툴팁 컴포넌트
  const CellWithDates = ({ row, month, issue }) => {
    const key = `${row.driver_name}__${row.car_number}__${month}__${issue}`;
    const list = cellItems[key] || [];
    const count = row.counts[month][issue] || 0;
    if (!count) return <span>{""}</span>;

    return (
      <div className="relative group inline-block">
        <span className="underline decoration-dotted cursor-help">{count}</span>
        {/* hover 팝오버 */}
        <div className="absolute z-[200] left-1/2 -translate-x-1/2 top-full mt-1 hidden group-hover:block bg-white border rounded shadow p-2 whitespace-nowrap">
          <div className="text-xs text-gray-600 mb-1">{month}월 {issue}</div>
          <div className="flex flex-wrap gap-1 max-w-[260px]">
            {list
              .sort((a, b) => new Date(a.date) - new Date(b.date))
              .map(({ id, date }) => {
                const label = new Date(date).toLocaleDateString("ko-KR", { month: "numeric", day: "numeric" });
                return (
                  <button
                    key={id}
                    onClick={() => navigate(`/safety/${id}`)}
                    className="px-2 py-0.5 text-xs rounded border hover:bg-gray-50"
                  >
                    {label}
                  </button>
                );
              })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-1">
      <Header />

      <div className="px-4 pt-1 pb-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">안전 관리</h2>
          <div className="flex items-center gap-2">
            <select
              className="border rounded px-2 py-1 text-sm"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              title="연도 선택"
            >
              {[-1,0,1].map(off => {
                const y = new Date().getFullYear() + off;
                return <option key={y} value={y}>{y}년</option>;
              })}
            </select>
            <Button variant="outline" onClick={fetchAll} disabled={loading}>
              {loading ? "조회중..." : "새로고침"}
            </Button>
            <Button onClick={() => navigate("/safety/new")}>새 글</Button>
          </div>
        </div>

        <Card>
          <CardContent className="bg-white">
            <div className="relative max-h-[70vh] overflow-auto">
              <table className="table-auto w-full border text-sm">
                <thead className="sticky top-0 z-30">
                  <tr className="bg-gray-100">
                    <th className="border p-2 align-middle" rowSpan={2}>차 번호</th>
                    <th className="border p-2 align-middle w-16 min-w-16 whitespace-nowrap" rowSpan={2}>이름</th>
                    {MONTHS.map((m) => (
                      <th key={`m_${m}`} className="border p-2 text-center" colSpan={ISSUES.length}>
                        {m}월
                      </th>
                    ))}
                    <th className="border p-2 align-middle" rowSpan={2}>합계</th>
                  </tr>
                  <tr className="bg-gray-50">
                    {MONTHS.map((m) =>
                      ISSUES.map((iss) => (
                        <th key={`m_${m}_${iss}`} className="border p-1 text-center">{iss}</th>
                      ))
                    )}
                  </tr>
                </thead>

                <tbody>
                  {rows.map((r, idx) => (
                    <tr key={`${r.driver_name}-${r.car_number}-${idx}`}>
                      <td className="border p-2">{r.car_number || "-"}</td>
                      <td className="border p-2 w-16 min-w-16 whitespace-nowrap">{r.driver_name || "-"}</td>
                      {MONTHS.map((m) =>
                        ISSUES.map((iss) => (
                          <td
                            key={`r_${idx}_${m}_${iss}`}
                            className="border p-1 text-center align-top relative group cursor-pointer"
                            >
                            {/* ▶ 숫자 + hover 날짜/클릭 이동 */}
                            <CellWithDates row={r} month={m} issue={iss} />
                          </td>
                        ))
                      )}
                      <td className="border p-2 text-center font-semibold">{printVal(r.total)}</td>
                    </tr>
                  ))}

                  {/* 합계 행 */}
                  <tr className="bg-gray-200 font-semibold">
                    <td className="border p-2 text-center">합계</td>
                    <td className="border p-2"></td>
                    {MONTHS.map((m) =>
                      ISSUES.map((iss) => (
                        <td key={`t_${m}_${iss}`} className="border p-1 text-center">
                          {printVal(colTotals[m][iss])}
                        </td>
                      ))
                    )}
                    <td className="border p-2 text-center">{printVal(colTotals.total)}</td>
                  </tr>

                  {rows.length === 0 && !loading && (
                    <tr>
                      <td className="border p-4 text-center text-gray-500" colSpan={2 + MONTHS.length * ISSUES.length + 1}>
                        데이터가 없습니다.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              * 집계 기준: {year}년 / 동일 회사·기사명·차량번호 / 이슈: {ISSUES.join("·")}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
